import { useCallback, useEffect, useState } from 'react';

/**
 * Easer home widgets: order and visibility are personal to this browser (localStorage).
 * `locked` widgets can be moved but never hidden (safety reminders: ongoing prestation, offline queue).
 */
export const HOME_WIDGETS = [
  { id: 'mood', label: 'Météo du moral', description: 'Votre humeur du jour, partagée avec l\'agence' },
  { id: 'satisfaction', label: 'Satisfaction client', description: 'Les notes laissées par vos clients et les contrôles qualité' },
  { id: 'qualite', label: 'Contrôles qualité', description: 'Les évaluations de vos interventions par un manager, avec réponse possible' },
  { id: 'reminders', label: 'Rappels du jour', description: 'Prestation en cours, notifications, documents, envois en attente', locked: true },
  { id: 'next', label: 'Prochaine intervention', description: 'Client, horaire, itinéraire et pointage' },
  { id: 'summary', label: 'Résumé de la journée', description: 'Interventions, terminées, tracts ou prochain congé' },
  { id: 'week', label: 'Ma semaine', description: 'Heures réalisées, interventions et avis reçus vs semaine dernière' },
  { id: 'weather', label: 'Météo du jour', description: 'Prévisions sur vos lieux d\'intervention' },
  { id: 'install', label: 'Installer l\'application', description: 'Raccourci vers l\'écran d\'accueil (masqué une fois installée)' },
];

export const DEFAULT_ORDER = HOME_WIDGETS.map((w) => w.id);
const STORAGE_KEY = 'pts_easer_home_layout';
const EVENT = 'easer:home-layout-changed';

const KNOWN = new Set(DEFAULT_ORDER);
const LOCKED = new Set(HOME_WIDGETS.filter((w) => w.locked).map((w) => w.id));

/** Normalise a stored layout: drop unknown ids, append new widgets, never hide locked ones. */
export function normalizeLayout(raw) {
  const order = Array.isArray(raw?.order) ? raw.order.filter((id, i, arr) => KNOWN.has(id) && arr.indexOf(id) === i) : [];
  DEFAULT_ORDER.forEach((id) => { if (!order.includes(id)) order.push(id); });
  const hidden = Array.isArray(raw?.hidden) ? raw.hidden.filter((id) => KNOWN.has(id) && !LOCKED.has(id)) : [];
  return { order, hidden };
}

export function loadLayout() {
  try {
    return normalizeLayout(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return normalizeLayout(null);
  }
}

export function saveLayout(layout) {
  const next = normalizeLayout(layout);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* storage full or private mode */ }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  return next;
}

export function resetLayout() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  const next = normalizeLayout(null);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  return next;
}

export const isDefaultLayout = (layout) => layout.hidden.length === 0 && layout.order.every((id, i) => id === DEFAULT_ORDER[i]);

/** React hook: current layout + setters, kept in sync across components and browser tabs. */
export function useHomeLayout() {
  const [layout, setLayout] = useState(loadLayout);

  useEffect(() => {
    const onLocal = (e) => setLayout(e.detail);
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setLayout(loadLayout()); };
    window.addEventListener(EVENT, onLocal);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener(EVENT, onLocal); window.removeEventListener('storage', onStorage); };
  }, []);

  const setOrder = useCallback((order) => saveLayout({ ...loadLayout(), order }), []);
  const setHidden = useCallback((id, hide) => {
    const cur = loadLayout();
    const hidden = hide ? [...new Set([...cur.hidden, id])] : cur.hidden.filter((h) => h !== id);
    saveLayout({ ...cur, hidden });
  }, []);
  const move = useCallback((id, delta) => {
    const cur = loadLayout();
    const i = cur.order.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= cur.order.length) return;
    const order = [...cur.order];
    [order[i], order[j]] = [order[j], order[i]];
    saveLayout({ ...cur, order });
  }, []);
  const reset = useCallback(() => resetLayout(), []);

  return { layout, setOrder, setHidden, move, reset, isDefault: isDefaultLayout(layout) };
}
